import React, { useState } from 'react';
import { Box, Button, Drawer } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { SearchFilterRail, type FacetKey } from '../search/SearchFilterRail';
import type { FacetBucket } from '../../contexts/SearchContext';

export interface CatalogFilterRailProps {
  /** The available facet buckets from the backend, keyed by facet name. */
  buckets: Record<string, FacetBucket[]> | null;
  /** Currently-selected values per facet. */
  selected: Record<FacetKey, Set<string>>;
  onToggle: (facet: FacetKey, value: string) => void;
  onClearAll: () => void;
}

/**
 * Thin wrapper mounting SearchFilterRail for the catalog pages
 * (GH#1263 PublicCatalog, GH#1265 My Objects). No `topSlot`/ScopeSelector —
 * the catalog page itself IS the scope, unlike the global /search page.
 *
 * Responsive treatment mirrors SearchPage's rail: a permanent rail at >= md,
 * and a "Filters" toggle button + MUI Drawer below md.
 */
export const CatalogFilterRail: React.FC<CatalogFilterRailProps> = ({
  buckets,
  selected,
  onToggle,
  onClearAll,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeTotal = (Object.keys(selected) as FacetKey[]).reduce(
    (sum, key) => sum + selected[key].size,
    0,
  );

  const rail = (
    <SearchFilterRail
      buckets={buckets}
      selected={selected}
      onToggle={onToggle}
      onClearAll={onClearAll}
    />
  );

  if (!isSmallScreen) {
    return <Box data-testid="catalog-filter-rail">{rail}</Box>;
  }

  return (
    <Box data-testid="catalog-filter-rail-mobile">
      <Button
        variant="outlined"
        size="small"
        startIcon={<FilterListIcon fontSize="small" />}
        onClick={() => setDrawerOpen(true)}
        data-testid="catalog-filter-rail-toggle"
        sx={{ textTransform: 'none' }}
      >
        Filters{activeTotal > 0 ? ` (${activeTotal})` : ''}
      </Button>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        aria-label="Filter catalog"
        data-testid="catalog-filter-rail-drawer"
      >
        <Box sx={{ width: 320, p: 2 }}>{rail}</Box>
      </Drawer>
    </Box>
  );
};

export default CatalogFilterRail;
